import { Painter, shade } from './pixel';
import { C } from './palette';

// Hand-authored forms at the game's native pixel scale. Colour clusters describe
// material and construction; details are placed deliberately instead of speckled.
const O = C.outline;
const W = { body: C.panel, dark: C.panelDk, edge: C.woodPaleDk, light: C.woodPale };
function carved(p: Painter, x: number, y: number, w: number, h: number) {
  p.panel(x, y, w, h, W.body, O, W.edge, W.dark);
  if (w > 9 && h > 9) {
    p.box(x + 3, y + 3, w - 6, h - 6, W.dark);
    p.hline(x + 4, y + 4, w - 8, W.edge).vline(x + 4, y + 4, h - 8, W.edge);
    p.px(x + w - 5, y + h - 5, W.light);
    p.rect(x + 3.5, y + 3.5, w - 7, 0.5, W.light);
    p.rect(x + 3.5, y + 3.5, 0.5, h - 7, W.light);
    p.rect(x + w - 4, y + 4, 0.5, h - 8, W.dark);
  }
}
function legs(p: Painter, w: number, y: number) {
  for (const x of [3, w - 6]) p.rect(x, y, 3, 4, O).vline(x, y, 3, W.edge).px(x - 1, y + 3, O);
}
function diamond(p: Painter, cx: number, cy: number, r: number, color: string) {
  for (let y = -r; y <= r; y++) p.hline(cx - r + Math.abs(y), cy + y, (r - Math.abs(y)) * 2 + 1, color);
}
function bookcase(id: string) {
  const p = new Painter(32, 40);
  carved(p, 1, 2, 30, 36);
  p.panel(0, 0, 32, 4, W.edge, O, W.light, W.dark);
  const colors = [C.carpetRed, C.fabricBlue, C.paperGreen, C.woodPale, C.cloth.purple, C.sheetSh];
  for (let row = 0; row < 3; row++) {
    const y = 6 + row * 10;
    p.rect(4, y - 1, 24, 9, O);
    let x = 4;
    for (let n = 0; x < 27; n++) {
      const width = 2 + ((n + row) % 3 === 0 ? 1 : 0), h = 5 + (n * 3 + row * 2) % 4;
      const col = colors[(n + row * 2 + (id === 'bookshelfLever' ? 2 : 0)) % colors.length];
      p.rect(x, y + 8 - h, width, h, shade(col, -0.25));
      p.vline(x, y + 8 - h, h - 1, col).hline(x, y + 9 - h, width, C.woodPale);
      if (h > 6) p.rect(x + 0.5, y + 6, width - 1, 0.5, C.sheetSh);
      p.rect(x + 0.5, y + 8 - h, 0.5, h - 1, shade(col, 0.3));
      p.rect(x + 0.5, y + 4, 0.5, 1.5, C.woodPale);
      x += width + 1;
    }
    p.hline(3, y + 8, 26, W.light).hline(3, y + 9, 26, W.dark);
  }
  p.vline(1, 4, 32, W.edge).vline(29, 4, 32, W.dark);
  p.hline(1, 37, 30, W.edge); legs(p, 32, 36);
  return p.done();
}
function wardrobe(push: boolean) {
  const p = new Painter(32, 40);
  p.rect(4, 0, 24, 2, O).hline(5, 1, 22, W.light);
  p.panel(1, 2, 30, 4, W.edge, O, W.light, W.dark);
  carved(p, 2, 6, 14, 30); carved(p, 16, 6, 14, 30);
  for (const x of [5, 19]) {
    p.box(x, 9, 8, 10, W.dark).hline(x + 1, 10, 6, W.edge);
    p.box(x, 25, 8, 7, W.dark).hline(x + 1, 26, 6, W.edge);
    p.px(x + 2, 12, W.light).vline(x + 3, 13, 3, W.edge).px(x + 4, 16, W.dark);
  }
  p.vline(15, 6, 29, O);
  for (const x of [12, 18]) p.rect(x, 20, 2, 4, C.brassDk).px(x, 20, C.brass).px(x, 21, C.sheet).px(x, 23, O);
  p.panel(1, 35, 30, 3, W.body, O, W.edge); legs(p, 32, 36);
  if (push) p.hline(6, 33, 5, W.dark).px(23, 18, W.dark).vline(7, 26, 4, W.dark);
  return p.done();
}
function sofa(red: boolean, chair = false) {
  const w = chair ? 16 : 48, h = chair ? 20 : 24;
  const p = new Painter(w, h), base = red ? C.fabricRed : chair ? C.fabricBlue : C.fabricGreen;
  const dk = shade(base, -0.35), hi = shade(base, 0.35), mid = shade(base, 0.12);
  legs(p, w, h - 4);
  p.panel(2, 1, w - 4, 11, base, O, hi, dk).hline(5, 0, w - 10, O);
  for (let x = 6; x < w - 4; x += 7) {
    p.rect(x, 5, 0.5, 0.5, dk).rect(x - 0.5, 4.5, 0.5, 0.5, hi);
    for (let n = 1; n < 3; n++) p.rect(x - n * 0.5, 5 + n * 0.5, 0.5, 0.5, dk).rect(x + n * 0.5, 5 + n * 0.5, 0.5, 0.5, dk);
  }
  const n = chair ? 1 : 3, cw = chair ? 8 : 12;
  for (let i = 0; i < n; i++) {
    const x = 4 + i * 12;
    p.panel(x, 10, cw, h - 14, mid, O, hi, dk);
    p.hline(x + 2, 12, cw - 4, base).px(x + cw - 3, h - 7, dk);
    p.rect(x + 1.5, 10.5, cw - 3, 0.5, hi).rect(x + 1, 11, 0.5, h - 16, hi);
    p.rect(x + 2, h - 5.5, cw - 4, 0.5, dk);
  }
  for (const x of [0, w - 5]) {
    p.panel(x, 8, 5, h - 10, base, O, hi, dk).hline(x + 1, 8, 3, hi);
    p.px(x + 2, h - 5, C.brassDk);
  }
  p.hline(5, h - 4, w - 10, dk).hline(5, h - 3, w - 10, W.edge);
  if (!chair) { // A folded cushion breaks the repeating upholstery.
    p.panel(7, 7, 7, 6, red ? C.woodPaleDk : C.sheetSh, dk, C.sheet);
    p.px(10, 9, C.brassDk);
  }
  return p.done();
}
function bed(w: number, h: number, metal = false) {
  const p = new Painter(w, h);
  const blue = w === 16, base = metal ? C.paperGreen : blue ? C.fabricBlue : C.fabricRed;
  const top = blue ? 11 : 19;
  carved(p, 1, 1, w - 2, 10);
  for (const x of [0, w - 3]) {
    p.rect(x, 1, 3, h - 2, metal ? C.metalDk : W.dark).hline(x, 0, 3, C.brass).vline(x, 2, 6, metal ? C.metalLt : W.light);
  }
  p.panel(2, 7, w - 4, h - 11, C.sheet, O, C.pillow, C.sheetSh);
  const pillows = w > 16 ? 2 : 1;
  for (let i = 0; i < pillows; i++) {
    const pw = w > 16 ? 11 : 10, x = 3 + i * 14;
    p.panel(x, 9, pw, blue ? 4 : 7, C.pillow, C.sheetSh, C.white);
    p.hline(x + 2, blue ? 11 : 14, pw - 4, C.sheet);
  }
  p.panel(2, top, w - 4, h - top - 5, base, O, shade(base, 0.35), shade(base, -0.3));
  p.rect(3, top, w - 6, 3, C.sheetSh).hline(3, top, w - 6, C.sheet);
  for (let x = 5; x < w - 4; x += 6) {
    p.rect(x, top + 5, 0.5, h - top - 12, shade(base, -0.13));
    p.px(x + 1, top + 6, shade(base, 0.2));
  }
  for (let y = top + 8; y < h - 8; y += 7) for (let x = 7; x < w - 5; x += 8) {
    p.px(x, y, C.woodPaleDk).px(x + 1, y + 1, shade(base, -0.3));
  }
  p.hline(2, h - 6, w - 4, shade(base, -0.4)).rect(2, h - 5, w - 4, 3, O);
  p.hline(1, h - 2, w - 2, W.edge);
  return p.done();
}
function fireplace() {
  const p = new Painter(48, 40);
  p.panel(2, 3, 44, 35, C.stoneDk, O, C.stone, C.stoneLine);
  p.panel(0, 0, 48, 5, C.stone, O, C.stoneLt, C.stoneLine).hline(2, 1, 44, C.sheetSh);
  p.panel(4, 6, 40, 5, C.stone, C.stoneLine, C.stoneLt);
  diamond(p, 24, 8, 2, C.stoneLt);
  for (const x of [4, 38]) {
    p.panel(x, 11, 6, 24, C.stone, C.stoneLine, C.stoneLt, C.stoneDk);
    p.vline(x + 2, 14, 17, C.stoneLt).hline(x, 32, 6, C.stoneLine);
  }
  p.rect(11, 14, 26, 21, O).rect(14, 11, 20, 4, O);
  p.hline(15, 12, 18, C.brickDk);
  for (let y = 17; y < 31; y += 5) p.hline(13, y, 22, C.brickDk);
  p.rect(13, 32, 22, 3, C.woodLine).hline(15, 32, 16, C.fireDk);
  for (let n = 0; n < 5; n++) {
    const x = 15 + n * 4, h = [8, 13, 10, 16, 7][n];
    for (let j = 0; j < h; j++) {
      const w = Math.max(1, Math.round((h - j) / 3));
      p.hline(x - Math.floor(w / 2) + (j > h / 2 ? 1 : 0), 31 - j, w + 1, j < 3 ? C.fireHi : j < h - 3 ? C.fire : C.fireDk);
    }
    p.px(x, 32, C.fireHi);
  }
  p.hline(11, 34, 26, C.metalDk);
  for (let x = 13; x < 37; x += 5) p.vline(x, 30, 5, C.metalDk).px(x, 30, C.metalLt);
  p.panel(0, 36, 48, 4, C.stoneDk, O, C.stoneLt);
  return p.done();
}
function plant() {
  const p = new Painter(16, 28);
  p.vline(7, 5, 13, C.leafDk).vline(8, 9, 10, C.leaf);
  const leaves = [[3, 3, 1], [9, 1, -1], [0, 8, 1], [10, 7, -1], [3, 11, 1], [10, 12, -1]];
  for (const [x, y, dir] of leaves) {
    p.rect(x + 1, y, 3, 1, C.leafDk).rect(x, y + 1, 5, 2, C.leafDk).rect(x + 1, y + 3, 3, 1, C.leafDk);
    p.hline(x + 1, y + 1, 3, C.leaf).px(x + (dir > 0 ? 1 : 3), y + 1, C.paperGreenLt);
  }
  p.panel(2, 17, 12, 4, C.pot, O, C.woodPale, C.potDk).hline(4, 17, 8, C.dirtDk);
  p.rect(3, 21, 10, 4, C.potDk).rect(4, 25, 8, 2, O).rect(4, 21, 7, 5, C.pot);
  p.vline(4, 21, 3, C.woodPaleDk).vline(11, 21, 4, C.potDk).hline(5, 26, 6, C.potDk);
  return p.done();
}
function rug(blue: boolean) {
  const p = new Painter(48, 32), base = blue ? C.carpetBlue : C.carpetRed;
  const dk = blue ? C.carpetBlueDk : C.carpetRedDk;
  p.panel(2, 1, 44, 30, base, dk, C.carpetGold, dk);
  p.box(4, 3, 40, 26, C.woodPaleDk).box(7, 6, 34, 20, dk);
  diamond(p, 24, 16, 9, dk); diamond(p, 24, 16, 7, C.woodPaleDk);
  diamond(p, 24, 16, 5, base); diamond(p, 24, 16, 2, C.carpetGold);
  for (const x of [11, 36]) { diamond(p, x, 10, 2, C.woodPaleDk); diamond(p, x, 22, 2, C.woodPaleDk); }
  for (let y = 3; y < 30; y += 3) p.hline(0, y, 2, C.sheetSh).hline(46, y, 2, C.sheetSh);
  for (let x = 6; x < 44; x += 4) p.px(x, 4, C.carpetGold).px(x, 27, C.carpetGold);
  return p.done();
}
function desk() {
  const p = new Painter(32, 24);
  legs(p, 32, 20);
  carved(p, 1, 9, 11, 13); carved(p, 20, 9, 11, 13);
  p.rect(12, 10, 8, 2, W.dark);
  p.panel(0, 4, 32, 7, W.body, O, W.light, W.dark).hline(2, 6, 28, W.edge);
  for (const x of [4, 23]) p.hline(x, 13, 5, W.edge).hline(x, 18, 5, W.dark).px(x + 2, 15, C.brass);
  p.panel(6, 1, 11, 6, C.paper, C.paperDk, C.sheet).vline(11, 2, 4, C.paperDk);
  p.hline(7, 3, 3, C.woodPaleDk).hline(12, 4, 3, C.woodPaleDk);
  p.rect(23, 3, 4, 4, C.night).hline(23, 3, 4, C.metalLt).px(24, 1, C.paper).px(25, 0, C.paper);
  return p.done();
}

function roundTable() {
  const p = new Painter(32, 30);
  p.rect(13, 14, 6, 13, W.dark).vline(14, 18, 8, W.edge);
  p.hline(8, 28, 16, O).hline(10, 27, 12, W.body).px(7, 29, O).px(24, 29, O);
  for (let y = 0; y < 18; y++) {
    const inset = [10, 6, 3, 2, 1, 0, 0, 0, 0, 0, 0, 1, 2, 3, 4, 6, 9, 12][y];
    p.hline(inset, y, 32 - inset * 2, y === 0 || y > 15 ? O : y > 12 ? W.dark : W.body);
    if (y > 0 && y < 12) p.px(inset, y, W.edge).px(31 - inset, y, W.dark);
  }
  p.hline(7, 2, 18, W.light).hline(3, 10, 5, W.edge).hline(23, 7, 5, W.dark);
  // Tea service and a folded linen napkin.
  p.panel(10, 5, 12, 6, C.sheetSh, C.woodPaleDk, C.sheet);
  p.rect(14, 4, 4, 5, C.metalDk).rect(14, 3, 4, 4, C.metal).px(14, 3, C.metalLt).px(18, 4, C.metal);
  p.rect(10, 7, 3, 2, C.pillow).px(11, 7, C.woodPaleDk).rect(19, 7, 3, 2, C.pillow);
  return p.done();
}
function cabinet() {
  const p = new Painter(32, 40);
  carved(p, 1, 3, 30, 34); p.panel(0, 0, 32, 4, W.edge, O, W.light, W.dark);
  p.panel(4, 6, 24, 18, C.night, W.dark, C.metalDk);
  for (const [x, y, col] of [[6, 10, C.sheet], [13, 9, C.brass], [22, 11, C.glass], [8, 19, C.glass], [19, 18, C.sheetSh]] as const) {
    p.rect(x, y, 3, 4, col).px(x, y - 1, col).px(x + 2, y + 2, C.metalDk);
  }
  p.hline(4, 15, 24, W.edge).vline(15, 6, 18, W.edge);
  p.vline(6, 7, 3, C.glass).px(7, 7, C.glassLt).vline(24, 16, 4, C.glass);
  for (let j = 0; j < 6; j++) {
    p.rect(6 + j * 0.5, 8 + j * 0.5, 0.5, 1.5, C.glass);
    p.rect(20 + j * 0.5, 17 + j * 0.5, 0.5, 0.5, C.glassLt);
  }
  carved(p, 4, 26, 11, 9); carved(p, 17, 26, 11, 9);
  p.px(12, 30, C.brass).px(19, 30, C.brass); legs(p, 32, 36);
  return p.done();
}
function clock() {
  const p = new Painter(16, 40);
  p.hline(5, 0, 6, O).hline(3, 1, 10, O).hline(2, 2, 12, W.edge);
  carved(p, 2, 3, 12, 34);
  p.panel(3, 4, 10, 11, C.brassDk, O, C.brass);
  p.rect(5, 5, 6, 9, C.sheet).rect(4, 7, 8, 5, C.sheet);
  p.px(8, 6, O).px(8, 12, O).px(5, 9, O).px(11, 9, O);
  p.vline(8, 7, 3, O).hline(8, 9, 3, O);
  p.panel(5, 17, 6, 16, C.night, W.dark, W.edge);
  p.vline(8, 19, 8, C.brassDk).rect(6, 27, 4, 4, C.brassDk).hline(7, 27, 2, C.brass);
  p.panel(1, 35, 14, 3, W.body, O, W.edge).px(2, 39, O).px(13, 39, O);
  return p.done();
}
function statue() {
  const p = new Painter(16, 40);
  p.art(0, 0, [
    '......OOOO......', '.....OLLsSO.....', '.....OLsSSO.....', '.....OLdSSO.....',
    '......OLSO......', '......OdSO......', '....OOLLLSOO....', '...OLLLLSSSSO...',
    '..OLLLLdSSSSSO..', '..OLLLddSSSSSO..', '..OLLLdSSSSSSO..', '...OLLLSSSSSO...',
    '....OLdSSSSO....', '....OLdSSSSO....', '....OLddSSSO....', '...OLLLddSSSO...',
    '...OLLLdSSSSO...', '...OLLdSSSSSO...', '...OLLdSSSSSO...', '...OLLddSSSSO...',
    '..OLLLLddSSSSO..', '..OLLLLdSSSSSO..', '..OLLLdSSSSSSO..', '..OLLLdSSSSSSO..',
    '..OLLLddSSSSSO..', '..OLLLLddSSSSO..', '..OOOOOOOOOOOO..',
  ], { O, L: C.stoneLt, S: C.stone, s: C.sheetSh, d: C.stoneDk });
  p.panel(1, 27, 14, 4, C.stone, O, C.stoneLt, C.stoneDk);
  p.panel(3, 31, 10, 6, C.stoneDk, O, C.stone, C.stoneLine).rect(5, 33, 6, 2, C.brassDk);
  p.panel(0, 37, 16, 3, C.stone, O, C.stoneLt);
  return p.done();
}

/** Small, staggered details animate in the object's own depth layer. */
export function animateFurniture(ctx: CanvasRenderingContext2D, id: string, x: number, y: number, time: number): void {
  const frame = Math.floor(time * 9 + x * 0.13) % 4;
  if (id === 'fireplace') {
    ctx.fillStyle = C.fire;
    for (let i = 0; i < 4; i++) {
      const dx = 17 + i * 4, dy = 20 + ((i + frame) % 3) * 2;
      ctx.fillRect(x + dx, y + dy, 2, 6);
      ctx.fillStyle = C.fireHi; ctx.fillRect(x + dx, y + dy + 5, 2, 3); ctx.fillStyle = C.fire;
    }
    ctx.fillStyle = C.fireDk; ctx.fillRect(x + 19 + frame * 3, y + 15 - frame, 1, 1);
  } else if (id === 'candelabra' || id === 'altar') {
    ctx.fillStyle = C.fireHi;
    const dx = id === 'altar' ? 24 : 8;
    ctx.fillRect(x + dx, y + 4 + frame % 2, 1, 2);
  } else if (id === 'cagedLamp') {
    ctx.fillStyle = '#e6b45f';
    ctx.fillRect(x + 5, y + 10, 3, 4); ctx.fillRect(x + 9, y + 10, 2, 4);
    ctx.fillRect(x + 5, y + 15, 3, 5); ctx.fillRect(x + 9, y + 15, 2, 5);
    ctx.fillStyle = '#fff0b0';
    ctx.fillRect(x + 6, y + 11, 1, 3); ctx.fillRect(x + 6, y + 15, 1, 3);
  } else if (id === 'clock') {
    ctx.fillStyle = C.night; ctx.fillRect(x + 6, y + 19, 4, 12);
    const dx = 8 + Math.round(Math.sin(time * 2.5) * 1);
    ctx.fillStyle = C.brassDk; ctx.fillRect(x + dx, y + 19, 1, 8);
    ctx.fillStyle = C.brass; ctx.fillRect(x + dx - 1, y + 27, 3, 3);
  }
}

function piano() {
  const p = new Painter(48, 36);
  const black = '#20232c', hi = '#64636b', mid = '#363944';
  legs(p,48,32);
  // The asymmetrical grand-piano case and curved tail are drawn as deliberate scanlines.
  for(let y=1;y<25;y+=0.5){
    const left=y<4?8-y*1.5:2;
    const right=y<7?30+y*1.5:y<16?43:46;
    p.rect(left,y,right-left,0.5,y<2?hi:y>22?C.outline:black);
    p.rect(left,y,0.5,0.5,hi).rect(right-0.5,y,0.5,0.5,C.outline);
  }
  p.rect(5,5,27,0.5,hi).rect(5,6,0.5,12,mid).rect(6,7,20,0.5,mid);
  p.rect(8,8,20,9,'#292c36').rect(9,8,18,0.5,hi);
  p.rect(7,20,38,1,C.brassDk).rect(7,21,38,2,C.outline);
  p.panel(2,23,44,10,black,C.outline,hi,mid);
  for(let n=0;n<26;n++){
    const x=4+n*1.5;
    p.rect(x,24,1.5,6,C.sheet).rect(x+1,24,0.5,6,C.sheetSh);
    if(![2,6].includes(n%7))p.rect(x+1,24,0.75,3.5,C.outline).rect(x+1,24,0.5,0.5,hi);
  }
  p.rect(4,30,39,0.5,C.brass).rect(4,31,39,0.5,mid);
  p.panel(16,14,14,6,C.paper,C.outline,C.sheet).vline(23,15,4,C.paperDk);
  for(const x of [17,24])for(let y=15;y<19;y+=1.5)p.rect(x,y,4,0.5,C.woodPaleDk);
  p.rect(19,33,2,2,C.brassDk).rect(25,33,2,2,C.brassDk);
  return p.done();
}
function barrel() {
  const p = new Painter(16,22);
  for(let y=1;y<21;y+=0.5){
    const inset=y<4?3-y*0.5:y>17?1+(y-17)*0.5:1;
    p.rect(inset,y,16-2*inset,0.5,W.body);
    p.rect(inset,y,0.5,0.5,W.edge).rect(15.5-inset,y,0.5,0.5,W.dark);
  }
  for(let x=4;x<14;x+=3)p.rect(x,3,0.5,16,W.dark).rect(x+0.5,4,0.5,14,W.edge);
  p.panel(3,0,10,4,W.edge,O,W.light,W.dark).rect(4,1,8,0.5,W.light);
  for(const y of [5,15]){
    p.rect(1,y,14,2.5,C.metalDk).rect(1,y,14,0.5,C.metalLt).rect(1,y+2,14,0.5,O);
    for(const x of [3,8,12])p.rect(x,y+1,0.5,0.5,C.metalLt);
  }
  p.rect(4,20.5,8,0.5,O).rect(5,2,4,0.5,W.dark);
  return p.done();
}
function crate(stack=false) {
  const p=new Painter(stack?32:16,stack?40:18);
  function box(x:number,y:number){
    p.panel(x,y,16,17,W.body,O,W.edge,W.dark);
    for(let dy=4;dy<15;dy+=4)p.rect(x+1,y+dy,14,0.5,W.dark).rect(x+1,y+dy+0.5,14,0.5,W.edge);
    for(let n=0;n<13;n+=0.5)p.rect(x+1+n,y+1+n,1.5,1.5,W.dark).rect(x+1+n,y+n,1.5,1,W.edge);
    p.rect(x+1,y+1,1,14,W.light).rect(x+14,y+1,1,14,W.edge);
    for(const dx of [2,13])for(const dy of [2,14])p.rect(x+dx,y+dy,0.5,0.5,C.metalLt);
    p.rect(x+3,y+16,10,1,O);
  }
  if(stack){box(0,23);box(16,23);box(8,6);}else box(0,1);
  return p.done();
}
function diningTable() {
  const p=new Painter(64,32);
  p.rect(4,22,3,9,W.dark).rect(57,22,3,9,W.dark).rect(4,23,0.5,7,W.light).rect(57,23,0.5,7,W.edge);
  p.panel(0,4,64,20,W.body,O,W.light,W.dark);
  p.panel(2,2,60,19,C.sheetSh,C.woodPaleDk,C.sheet,C.paperDk);
  p.rect(27,2,10,19,C.carpetRed).rect(28,2,0.5,19,C.carpetGold).rect(35.5,2,0.5,19,C.carpetGold);
  for(const x of [8,21,43,56])for(const y of [7,16]){
    for(let dy=-2;dy<=2;dy+=0.5){const rx=Math.sqrt(Math.max(0,1-dy*dy/4))*3;p.rect(x-rx,y+dy,rx*2,0.5,C.stoneLt);}
    p.rect(x-2,y-1.5,4,2.5,C.pillow).rect(x-1.5,y-1,3,1.5,C.sheetSh);
    p.rect(x-4.5,y-2,0.5,4,C.metalLt).rect(x+4,y-2,0.5,4,C.metal);
    p.rect(x+3,y-4,1.5,1.5,C.glass).rect(x+3.5,y-2.5,0.5,1,C.glassLt);
  }
  p.rect(31,5,2,11,C.brassDk).rect(30,14,4,1,C.brass).rect(31,5,1,5,C.pillow).px(31,4,C.fire);
  for(let x=4;x<62;x+=7)p.rect(x,20,0.5,3,C.paperDk).rect(x+0.5,21,0.5,2,C.sheet);
  return p.done();
}

export function furnitureSprite(id: string): HTMLCanvasElement | null {
  switch (id) {
    case 'bookshelf': case 'bookshelfSecret': case 'bookshelfLever': return bookcase(id);
    case 'wardrobe': case 'wardrobePush': return wardrobe(id === 'wardrobePush');
    case 'sofa': case 'sofaRed': return sofa(id === 'sofaRed');
    case 'armchair': return sofa(false, true);
    case 'bed': return bed(32, 48);
    case 'bedSmall': return bed(16, 32);
    case 'cot': return bed(16, 32, true);
    case 'fireplace': return fireplace();
    case 'plant': return plant();
    case 'rug': case 'rugBlue': return rug(id === 'rugBlue');
    case 'desk': return desk();
    case 'piano': return piano();
    case 'barrel': return barrel();
    case 'crate': return crate();
    case 'crateStack': return crate(true);
    case 'tableLong': return diningTable();
    case 'tableRound': return roundTable();
    case 'cabinet': return cabinet();
    case 'clock': return clock();
    case 'statue': return statue();
    default: return null;
  }
}
